import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { CalendarDays, LayoutDashboard, LockKeyhole, LogOut, Mail, Percent, Settings, Shirt, Users } from "lucide-react";
import { Seo } from "../site/Seo";
import { API } from "../../lib/eventUtils";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/events", label: "Events", icon: CalendarDays },
  { to: "/admin/signups", label: "Signups", icon: Users },
  { to: "/admin/merch", label: "Merch", icon: Shirt },
  { to: "/admin/affiliates", label: "Affiliate Codes", icon: Percent },
  { to: "/admin/subscribers", label: "Subscribers", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export const AdminShell = ({ title, children }) => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [authError, setAuthError] = useState(location.state?.authError || "");

  useEffect(() => {
    if (user) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (e) {
        if (e.response?.status === 403) setAuthError(e.response.data.detail);
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
    }
  };

  if (checking) {
    return (
      <div className="pt-16 min-h-[70vh] flex items-center justify-center" data-testid="admin-checking">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-zinc-500 animate-pulse">Checking access…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-16 min-h-[70vh] flex items-center justify-center px-5" data-testid="admin-login-screen">
        <Seo title="Organizer Login | HyroxNinja" description="Private owner dashboard for HyroxNinja event partners." noindex />
        <div className="border border-white/10 bg-[#0c0c0c] p-8 sm:p-10 max-w-md w-full text-center">
          <LockKeyhole size={32} className="text-volt mx-auto mb-5" />
          <h1 className="font-head font-black uppercase tracking-tight text-3xl mb-2">Organizer Access</h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-7">
            This dashboard is restricted to HyroxNinja organizers. Sign in with your authorized Google account.
          </p>
          {authError && (
            <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-5" data-testid="admin-auth-error">
              {authError}
            </p>
          )}
          <button onClick={login} className="btn-volt w-full justify-center" data-testid="admin-google-login">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-shell" className="pt-16 min-h-screen">
      <Seo title={`Owner Dashboard — ${title} | HyroxNinja`} description="Private HyroxNinja owner dashboard." noindex />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:grid lg:grid-cols-[230px_1fr] lg:gap-10 lg:items-start">
        <aside className="mb-8 lg:mb-0 lg:sticky lg:top-24">
          <p className="eyebrow mb-4">Owner Dashboard</p>
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0 -mx-1 px-1" data-testid="admin-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 whitespace-nowrap px-3.5 py-2.5 font-head uppercase tracking-wide text-sm font-bold border transition-colors ${
                    isActive ? "border-volt/60 text-volt bg-volt/5" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
                  }`
                }
              >
                <n.icon size={15} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 pt-5 border-t border-white/10 hidden lg:block">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3 break-all" data-testid="admin-user-email">
              {user.email}
            </p>
            <button onClick={logout} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid="admin-logout-button">
              <LogOut size={13} /> Log Out
            </button>
          </div>
        </aside>
        <main className="min-w-0 pb-16">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-3xl sm:text-4xl" data-testid="admin-page-title">
              {title}
            </h1>
            <button onClick={logout} className="btn-ghost !py-2 !px-3.5 !text-xs lg:hidden" data-testid="admin-logout-mobile">
              <LogOut size={13} /> Log Out
            </button>
          </div>
          {typeof children === "function" ? children(user) : children}
        </main>
      </div>
    </div>
  );
};
